import type {
  DirectoryItem,
  FileTreePayload,
  LayoutPayload,
  ReposFileTreeData,
  ReposFileTreeEntry,
} from '@github-ui/code-view-types'
import {repositoryTreePath} from '@github-ui/paths'
import {getQueryClient} from '@github-ui/query-client'
import {
  type QueryDepsFn,
  type QueryRouteQueryConfig,
  QueryRouteQueryType,
} from '@github-ui/react-core/future/data-router-types'
import {fetchTree} from '@github-ui/repos-file-tree-view/loaders/fetch-tree'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

import {codeViewFileTreeLayoutRoute} from '../routes/code-view-file-tree-layout-route'
import {codeViewLayoutRoute} from '../routes/code-view-layout-route'
import type {CodeViewPathParams} from './types/shared-query-types'

export type FileTreeQueryDeps = {
  pathname: string
  params: CodeViewPathParams
}

interface MissingItems {
  fileTree: ReposFileTreeData
  foldersToFetch: string[]
}

export interface FileTreeCacheData {
  fileTree: ReposFileTreeData
  foldersToFetch: string[]
  fileTreeProcessingTime: number
}

export function fileTreeCacheKey(owner: string, repo: string, branch: string) {
  return [owner, repo, branch, 'fileTree'] as const
}

const EMPTY_RESPONSE = null
const EMPTY_TREE: MissingItems = {fileTree: {}, foldersToFetch: []}

type FileTreeQuery = QueryRouteQueryConfig<
  string,
  string,
  string,
  'fileTree',
  QueryDepsFn<string>,
  null,
  QueryRouteQueryType
>

/**
 * Route-level query that fires during navigation to prefetch file tree data.
 * This is a best-effort optimization — if the layout data isn't cached yet,
 * it returns early and the useFileTree hook handles loading at render time.
 */
export const fileTreeQuery: FileTreeQuery = {
  queryName: 'fileTree',
  queryDeps: ({pathname, params}) => ({pathname: `${pathname}/_file_tree`, params}),
  queryFn: async ({queryDeps}) => {
    const {params} = queryDeps as FileTreeQueryDeps

    await updateFileTree({owner: params.owner, repo: params.repo, params})

    return EMPTY_RESPONSE
  },
  staleTime: 0,
  gcTime: 0, // no need to keep in memory
}

/**
 * Called by the route-level query during navigation. Resolves the branch name
 * from the layout cache, seeds the dedicated file tree cache if needed, then
 * fetches any missing tree segments for the current path.
 */
export async function updateFileTree({
  owner,
  repo,
  params,
}: {
  owner: string
  repo: string
  params: Pick<CodeViewPathParams, 'branch' | '*'>
}) {
  const queryClient = getQueryClient()

  const layoutQueryKey = codeViewLayoutRoute.queries.mainQuery.makeQueryKey({
    pathname: `/${owner}/${repo}/_layout`,
  })
  const layoutData = queryClient.getQueryData<{payload: LayoutPayload}>(layoutQueryKey)
  if (!layoutData) return

  const branch = layoutData.payload.refInfo.name
  const path = extractPathFromParams(params, branch)

  // Seed the dedicated cache from the layout if it hasn't been initialised yet
  const cacheKey = fileTreeCacheKey(owner, repo, branch)
  if (!queryClient.getQueryData<FileTreeCacheData>(cacheKey)) {
    const fileTreeLayoutQueryKey = codeViewFileTreeLayoutRoute.queries.mainQuery.makeQueryKey({
      pathname: `/${owner}/${repo}/_file_tree_layout`,
    })
    const fileTreeLayoutData = queryClient.getQueryData<{payload: FileTreePayload}>(fileTreeLayoutQueryKey)
    if (!fileTreeLayoutData) return

    queryClient.setQueryData<FileTreeCacheData>(cacheKey, {
      fileTree: fileTreeLayoutData.payload.fileTree,
      foldersToFetch: fileTreeLayoutData.payload.foldersToFetch,
      fileTreeProcessingTime: fileTreeLayoutData.payload.fileTreeProcessingTime,
    })
  }

  await loadFileTreePath({owner, repo, branch, path})
}

/**
 * Fetches missing file tree segments for the given path and merges them into
 * the dedicated file tree cache. Can be called from the route query (navigation
 * time), from the useFileTree hook (render time), or imperatively (e.g. after
 * a web commit).
 */
export async function loadFileTreePath({
  owner,
  repo,
  branch,
  path,
}: {
  owner: string
  repo: string
  branch: string
  path: string
}) {
  const queryClient = getQueryClient()
  const cacheKey = fileTreeCacheKey(owner, repo, branch)
  const cached = queryClient.getQueryData<FileTreeCacheData>(cacheKey)

  const fileTree = cached?.fileTree ?? {}
  const pathsToLoad = getPathsToLoad(path, fileTree)

  if (!pathsToLoad) return

  const inflightKey = `${owner}/${repo}:${branch}:${pathsToLoad.start}:${pathsToLoad.stop}`
  if (inflight.has(inflightKey)) return

  const promise = fetchMissingItems({
    owner,
    repo,
    branch,
    pathsToLoad,
  })
  inflight.set(inflightKey, promise)

  try {
    const {fileTree: newFileTree, foldersToFetch} = await promise

    queryClient.setQueryData<FileTreeCacheData>(cacheKey, old => {
      if (!old) {
        return {fileTree: newFileTree, foldersToFetch, fileTreeProcessingTime: 0}
      }
      return {
        ...old,
        fileTree: mergeFileTree(old.fileTree, newFileTree),
        foldersToFetch: [...new Set([...old.foldersToFetch, ...foldersToFetch])],
      }
    })
  } finally {
    inflight.delete(inflightKey)
  }
}

const inflight = new Map<string, Promise<MissingItems>>()

export async function fetchMissingItems({
  owner,
  repo,
  branch,
  pathsToLoad,
}: {
  owner: string
  repo: string
  branch: string
  pathsToLoad: {start: string; stop: string}
}): Promise<MissingItems> {
  if (pathsToLoad.start === pathsToLoad.stop) {
    return fetchSingleItem({owner, repo, branch, path: pathsToLoad.start})
  }

  const response = await verifiedFetchJSON(`/${owner}/${repo}/_file_tree`, {
    method: 'POST',
    body: {
      branch,
      start: pathsToLoad.start,
      stop: pathsToLoad.stop,
    },
  })

  if (!response.ok) {
    return EMPTY_TREE
  }

  const data = await response.json()

  if (!data?.payload?.fileTree) {
    throw new Error(`Unable to find payload for fileTree query`)
  }

  return data.payload.fileTree as FileTreePayload
}

async function fetchSingleItem({
  owner,
  repo,
  branch,
  path,
}: {
  owner: string
  repo: string
  branch: string
  path: string
}): Promise<MissingItems> {
  const url = repositoryTreePath({
    repo: {
      ownerLogin: owner,
      name: repo,
    },
    commitish: branch,
    action: 'tree',
    path,
  })
  const response = await fetchTree(url)

  return {
    fileTree: {
      [path]: response.tree,
    },
    foldersToFetch: [],
  }
}

export function mergeFileTree(oldTree: ReposFileTreeData, newTree: ReposFileTreeData) {
  const fileTree = {...oldTree}
  const newPaths = Object.keys(newTree).sort((a, b) => (a < b ? 1 : -1))

  for (const path of newPaths) {
    const {dir, name} = pathInfo(path)

    // if directory already exist in the fileTree, add itself to the entry
    const existingDir = fileTree[dir]
    if (existingDir) {
      fileTree[dir] = mergeDirectory(existingDir, {path, name, contentType: 'directory' as const})
    }

    // add new entry to fileTree
    if (newTree[path]) fileTree[path] = newTree[path]
  }

  return fileTree
}

export function mergeDirectory(entry: ReposFileTreeEntry, newItem: DirectoryItem): ReposFileTreeEntry {
  // Check if item already exists
  if (entry.items.some(item => item.path === newItem.path)) {
    return entry
  }

  const items = [...entry.items, newItem]

  items.sort((a, b) => {
    if (a.contentType === b.contentType) {
      return a.name < b.name ? -1 : 1
    }
    if (a.contentType === 'directory' && b.contentType !== 'directory') {
      return -1
    }
    return 1
  })

  return {
    items,
    totalCount: items.length,
  }
}

/**
 * Given a fileTree and a path, returns the range of paths that need to be loaded.
 * Returns null if everything is already loaded.
 */
export function getPathsToLoad(
  path: string | undefined,
  fileTree: ReposFileTreeData,
): {start: string; stop: string} | null {
  if (!path) return null

  const pathSegments = path.split('/')
  let currentPath = ''
  let parentPath = ''
  let stopPath: string | null = null

  for (const segment of pathSegments) {
    currentPath = currentPath ? `${currentPath}/${segment}` : segment

    // If this path is already loaded, skip it
    if (currentPath in fileTree) {
      parentPath = currentPath
      continue
    }

    // Check the parent directory to see if this item exists
    const parentData = fileTree[parentPath]
    if (parentData) {
      const item = parentData.items.find(it => it.path === currentPath)
      if (item) {
        // If we know it's a file, we don't need to load anything
        if (item.contentType === 'file') {
          break
        }
        // Item is a known directory but not loaded yet - this becomes the stop point
      } else {
        // Parent is loaded but doesn't contain this item (truncated or new file)
        // We need to reload the parent directory
        stopPath = parentPath
        break
      }
    }

    // First missing path becomes the stop so we know when to stop ascending in the backend
    if (stopPath === null) {
      stopPath = currentPath
    }

    parentPath = currentPath
  }

  if (stopPath === null) return null

  // start = leaf node, stop = first missing ancestor
  return {start: path, stop: stopPath}
}

function pathInfo(path: string) {
  const segments = path.split('/')
  const name = segments.pop() || ''
  return {dir: segments.join('/'), name}
}

function extractPathFromParams(params: Pick<CodeViewPathParams, 'branch' | '*'>, branch: string) {
  const pathWithBranch = `${params.branch}/${params['*'] || ''}`
  // +1 for the slash after the branch name
  return pathWithBranch.startsWith(branch) ? pathWithBranch.slice(branch.length + 1) : ''
}
